import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { debounce, groupBy } from 'lodash-es';

import type { SavedSearchGroupItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';

import InterObserver from '../../components/inter_observer/inter_observer';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import { NoContentPanelCompact } from '../../components/no_content_panel_compact/no_content_panel_compact';
import SelectableItems from '../../components/selectable_items/selectable_items';
import { SAVED_SEARCHES } from '../../constants/help_urls';
import { NEW_SAVED_SEARCH_MODAL } from '../../constants/modals';
import { SAVED_SEARCH_PAGE_ROUTE } from '../../constants/routes';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  getSavedSearches,
  getSavedSearchesNextPage,
} from '../../ducks/saved_search/thunks';
import {
  collapseGroup,
  expandGroup,
} from '../../ducks/saved_search_groups/slice';
import {
  changeSavedSearchGroup,
  getSavedSearchGroups,
  ungroupSavedSearches,
} from '../../ducks/saved_search_groups/thunks';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { selectUserRoles } from '../../utils/get_user_roles';
import SearchInputPanels from '../search_input_panels/search_input_panels';
import SavedSearchContextMenu from './context_menu';
import SavedSearch from './saved_search';
import SavedSearchGroup from './saved_search_group';
import { selectSavedSearchesAndGroups } from './selectors';
import UngroupedSavedSearch from './ungrouped_saved_search';

import emptySavedSearchesImageSvg from '../../images/empty_saved_searches.svg';

import styles from './saved_searches.module.css';

export function SavedSearchesPanel(): ReactNode {
  const dispatch = useAppDispatch();
  // Route and id come from the same match: see PanelsContainer.
  const { route, params } = useLeafRouteParams();
  const routeId = params.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loadingGroupIds, setLoadingGroupIds] = useState<
    Record<string, boolean>
  >({});

  const items = useAppSelector(selectSavedSearchesAndGroups);
  const savedSearches = useAppSelector(
    (state) => state.savedSearchesReducer.savedSearches,
  );
  const savedSearchesQuery = useAppSelector(
    (state) => state.savedSearchesReducer.savedSearchesQuery,
  );
  const groupsUnsorted = useAppSelector(
    (state) => state.savedSearchGroups.groups,
  );
  const groupsSorted = useAppSelector(
    (state) => state.savedSearchGroups.groupsSorted,
  );
  const expandedGroupIds = useAppSelector(
    (state) => state.savedSearchGroups.expandedGroupIds,
  );
  const roles = useAppSelector(selectUserRoles);
  const isSavedSearchRoute = route === SAVED_SEARCH_PAGE_ROUTE;
  const activeSearchGroupId = useAppSelector((state) =>
    isSavedSearchRoute && routeId
      ? (state.entities.saved_searches[routeId]?.group_id ?? null)
      : null,
  );

  const savedSearchId = isSavedSearchRoute ? routeId : undefined;
  const isSearchMode = Boolean(savedSearchesQuery);
  const groups = isSearchMode ? groupsSorted : groupsUnsorted;

  const trackLoading = useCallback(
    (promise: Promise<unknown> | undefined, reload = false) => {
      if (!promise) {
        return;
      }
      if (reload) {
        setIsReloading(true);
      } else {
        setIsLoading(true);
      }
      promise.finally(() => {
        setIsLoading(false);
        setIsReloading(false);
      });
    },
    [],
  );

  const isGroupLoading = useCallback(
    (group: SavedSearchGroupItem) => Boolean(loadingGroupIds[group.id]),
    [loadingGroupIds],
  );

  const trackGroupLoading = useCallback(
    (group: SavedSearchGroupItem, promise: Promise<unknown> | undefined) => {
      if (!promise) {
        return;
      }
      setLoadingGroupIds((previous) => ({ ...previous, [group.id]: true }));
      promise.finally(() => {
        setLoadingGroupIds((previous) => ({ ...previous, [group.id]: false }));
      });
    },
    [],
  );

  const handleGroupExpand = useCallback(
    (group: SavedSearchGroupItem) => {
      if (isGroupLoading(group)) {
        return;
      }
      if (!isSearchMode) {
        trackGroupLoading(
          group,
          dispatch(getSavedSearches({ groupId: group.id })),
        );
      }
      dispatch(expandGroup(group));
    },
    [isGroupLoading, isSearchMode, trackGroupLoading],
  );

  // Initial load only: later queries go through handleSearch, and re-running
  // this when savedSearchesQuery changes would refetch its own result.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    const promise = Promise.all([
      dispatch(getSavedSearches({ groupId: null, query: savedSearchesQuery })),
      dispatch(getSavedSearchGroups()),
    ]);
    trackLoading(promise, true);
  }, []);

  // Expand the group holding the saved search the URL points at.
  // handleGroupExpand and expandedGroupIds are intentionally excluded: their
  // identity changes with group loading state, which re-triggers this in a loop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (
      !activeSearchGroupId ||
      expandedGroupIds.includes(activeSearchGroupId)
    ) {
      return;
    }
    const group = groups.find((item) => item.id === activeSearchGroupId);
    if (group) {
      handleGroupExpand(group);
    }
  }, [activeSearchGroupId, groups]);

  const handleScrollEnds = useCallback(() => {
    if (isLoading || isReloading) {
      return;
    }
    trackLoading(dispatch(getSavedSearchesNextPage({ groupId: null })));
  }, [isLoading, isReloading, trackLoading]);

  const isFetchingRef = useRef(false);
  isFetchingRef.current = isLoading || isReloading;

  const handleSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (isFetchingRef.current) {
          return;
        }
        trackLoading(dispatch(getSavedSearches({ query })), true);
      }, 400),
    [trackLoading],
  );

  const setSelectedRows = useCallback((newSelectedIds: string[]) => {
    setSelectedIds((previous) =>
      previous.length !== 0 || newSelectedIds.length !== 0
        ? newSelectedIds
        : previous,
    );
  }, []);

  const handleGroupScrollEnds = useCallback(
    (group: SavedSearchGroupItem) => {
      if (isGroupLoading(group)) {
        return;
      }
      trackGroupLoading(
        group,
        dispatch(getSavedSearchesNextPage({ groupId: group.id })),
      );
    },
    [isGroupLoading, trackGroupLoading],
  );

  const handleDrop = useCallback(
    (targetId: string) => {
      savedSearches
        .filter(
          (savedSearch) =>
            selectedIds.includes(savedSearch.id) &&
            savedSearch.group_id !== targetId,
        )
        .forEach((savedSearch) => {
          dispatch(changeSavedSearchGroup(savedSearch.id, targetId));
        });
    },
    [savedSearches, selectedIds],
  );

  const handleMoveOutOfGroup = useCallback(() => {
    if (selectedIds.length < 1) {
      return;
    }
    dispatch(ungroupSavedSearches(selectedIds));
  }, [selectedIds]);

  const openNewSavedSearchModal = useCallback(() => {
    dispatch(openModal({ id: NEW_SAVED_SEARCH_MODAL, contextItems: [] }));
  }, []);

  const lastSavedSearch = savedSearches[savedSearches.length - 1];
  const lastSavedSearchId =
    isSearchMode && lastSavedSearch ? lastSavedSearch.id : undefined;

  const savedSearchesByGroupId = groupBy(savedSearches, 'group_id');
  const ungroupedSavedSearches = savedSearches.filter(
    (savedSearch) => !savedSearch.group_id,
  );

  const hasNonPreLoadedContent = () => {
    switch (savedSearches.length) {
      case 0:
        return false;
      case 1:
        return !savedSearches[0].criteria?.filter?.terms?.some((term) => {
          return term.name === 'metadata.iconikTutorialContent';
        });
      default:
        return true;
    }
  };

  return (
    <div
      className={styles.root}
      aria-busy={isLoading || isReloading ? true : undefined}
    >
      <SearchInputPanels
        searchText={savedSearchesQuery || ''}
        showNewButton={roles.canWriteSavedSearches}
        isNewButtonDisabled={false}
        onTextInput={handleSearch}
        onNewButtonClick={openNewSavedSearchModal}
      />
      <SelectableItems
        className={styles.list}
        contextMenu={SavedSearchContextMenu}
        onSelectIds={setSelectedRows}
        items={items}
        multiselect
        isSelectAllAllowed={false}
        aria={{
          label: 'Saved search items',
        }}
      >
        {isReloading && <LoadingBar className={styles.reloading} />}
        {groups.map((group) => {
          const savedSearchesOfGroup = savedSearchesByGroupId[group.id];
          if (
            isSearchMode &&
            (!savedSearchesOfGroup || savedSearchesOfGroup.length === 0)
          ) {
            return null;
          }
          return (
            <SavedSearchGroup
              key={group.id}
              group={group}
              isSelected={selectedIds.includes(group.id)}
              isCollapsed={!expandedGroupIds.includes(group.id)}
              onExpand={handleGroupExpand}
              onCollapse={(collapsedGroup) =>
                dispatch(collapseGroup(collapsedGroup))
              }
              onDrop={() => handleDrop(group.id)}
            >
              {savedSearchesOfGroup
                ? savedSearchesOfGroup.map((groupItem, groupItemIndex) => {
                    let result = (
                      <SavedSearch
                        key={groupItem.id}
                        item={groupItem}
                        isSelected={selectedIds.includes(groupItem.id)}
                        isActive={groupItem.id === savedSearchId}
                        isIndented
                      />
                    );
                    if (
                      !isSearchMode &&
                      groupItemIndex === savedSearchesOfGroup.length - 1
                    ) {
                      result = (
                        <InterObserver
                          key={groupItem.id}
                          onIntersect={() => handleGroupScrollEnds(group)}
                        >
                          {result}
                        </InterObserver>
                      );
                    }
                    if (isSearchMode && lastSavedSearchId === groupItem.id) {
                      result = (
                        <InterObserver
                          key={groupItem.id}
                          onIntersect={handleScrollEnds}
                        >
                          {result}
                        </InterObserver>
                      );
                    }

                    return result;
                  })
                : null}

              {loadingGroupIds[group.id] ? (
                <LoadingBar className={styles.loading} />
              ) : null}

              {!loadingGroupIds[group.id] && !savedSearchesOfGroup ? (
                <div className={styles.empty_group}>Empty group</div>
              ) : null}
            </SavedSearchGroup>
          );
        })}
        <UngroupedSavedSearch onDrop={handleMoveOutOfGroup}>
          {ungroupedSavedSearches.map((ungroupedItem, index) => {
            let result = (
              <SavedSearch
                key={ungroupedItem.id}
                item={ungroupedItem}
                isSelected={selectedIds.includes(ungroupedItem.id)}
                isActive={ungroupedItem.id === savedSearchId}
                onDrop={handleDrop}
              />
            );
            if (index === ungroupedSavedSearches.length - 1) {
              result = (
                <InterObserver
                  key={ungroupedItem.id}
                  onIntersect={handleScrollEnds}
                >
                  {result}
                </InterObserver>
              );
            }
            return result;
          })}
        </UngroupedSavedSearch>
        {isSearchMode && !isReloading && savedSearches.length === 0 && (
          <NoContentPanel
            imageSrc={emptySavedSearchesImageSvg}
            title="No saved searches found"
          />
        )}
      </SelectableItems>

      {isLoading && <LoadingBar className={styles.loading} />}
      {!isSearchMode &&
      !hasNonPreLoadedContent() &&
      !isLoading &&
      !isReloading ? (
        <NoContentPanelCompact
          title="After searching or filtering, create a saved search to easily access the results anytime"
          actionText="SAVE SEARCH"
          linkHref={SAVED_SEARCHES}
          linkText="Learn more"
          onActionClick={openNewSavedSearchModal}
        />
      ) : null}
    </div>
  );
}

export default SavedSearchesPanel;
